export interface VisaRequirement {
  label: string;
  value: string;
}

export interface VisaData {
  slug: string;
  country: string;
  title: string;
  tagline: string;
  heroImage: string;
  bannerImage: string;
  price: string;
  processingTime: string;
  validity: string;
  stayDuration: string;
  visaType: string;
  requirements: string[];
  highlights: string[];
  description: string;
  extraInfo: string;
}

export const VISA_LIST: VisaData[] = [
  {
    slug: "canada-visit-visa",
    country: "Canada",
    title: "Canada Visit Visa",
    tagline: "From the Rockies to Niagara — explore the Great White North",
    heroImage: "https://public.readdy.ai/ai/img_res/62372e5fef6f5a7542141389160dff1c.jpg",
    bannerImage: "https://readdy.ai/api/search-image?query=Canada%20Toronto%20CN%20Tower%20cityscape%20Niagara%20Falls%20winter%20snow%20travel%20destination%20wide%20angle%20beautiful%20skyline%20vibrant&width=600&height=400&seq=visa-detail-canada-side&orientation=landscape",
    price: "60,000",
    processingTime: "8 – 12 weeks",
    validity: "10 years",
    stayDuration: "Up to 6 months per visit",
    visaType: "Temporary Resident Visa (TRV)",
    requirements: [
      "Valid Pakistani passport (min. 6 months validity)",
      "Completed online application form",
      "Recent passport-size photographs",
      "Proof of funds (bank statements – last 6 months)",
      "Employment or business ownership proof",
      "Travel itinerary & hotel bookings",
      "Travel insurance coverage",
      "Property or asset documents",
      "No-objection certificate (if employed)",
    ],
    highlights: [
      "Covers all Canadian provinces & territories",
      "Multiple entry allowed",
      "Extendable inside Canada",
      "Family applications accepted",
    ],
    description: "Canada is one of the world's most sought-after travel destinations — from the iconic Niagara Falls and the Rocky Mountains to the vibrant cities of Toronto, Vancouver, and Montreal. A Canada Visit Visa (Temporary Resident Visa) allows Pakistani nationals to explore the country for tourism, visiting family, or business purposes. flightfare.pk provides end-to-end visa assistance so you can focus on planning your adventure.",
    extraInfo: "Canada operates a points-based immigration system. For visit visas, the consulate evaluates your ties to Pakistan (property, family, employment) to ensure you will return after your visit. Our team helps you present the strongest possible application.",
  },
  {
    slug: "spain-visit-visa",
    country: "Spain",
    title: "Spain Visit Visa",
    tagline: "Flamenco, tapas, and golden coastlines — Spain awaits",
    heroImage: "https://readdy.ai/api/search-image?query=Spain%20Seville%20Alcazar%20palace%20gardens%20warm%20golden%20afternoon%20light%20Andalusia%20travel%20destination%20beautiful%20historic%20architecture%20vibrant%20orange%20trees%20courtyard%20stunning&width=1200&height=500&seq=visa-detail-spain-hero&orientation=landscape",
    bannerImage: "https://readdy.ai/api/search-image?query=Spain%20Barcelona%20Park%20Guell%20Gaudi%20colourful%20mosaic%20tiles%20panoramic%20city%20view%20travel%20destination%20sunny%20Mediterranean%20vibrant%20art%20architecture&width=600&height=400&seq=visa-detail-spain-side&orientation=landscape",
    price: "60,000",
    processingTime: "15 working days",
    validity: "Single or multiple entry",
    stayDuration: "Up to 90 days in 180 days",
    visaType: "Schengen Short-Stay Visa (Type C)",
    requirements: [
      "Valid Pakistani passport (min. 3 months beyond travel)",
      "Completed Schengen visa application form",
      "2 recent biometric photographs",
      "Bank statements (last 3 months, min. PKR 500,000)",
      "Confirmed return flight ticket",
      "Hotel or accommodation bookings",
      "Travel insurance (min. EUR 30,000 coverage)",
      "Employment/business proof & NOC letter",
      "Income tax returns (last 2 years)",
    ],
    highlights: [
      "Schengen visa valid for 26+ European countries",
      "Apply through VFS Spain",
      "Multiple entry options available",
      "Strong tourism record boosts approval",
    ],
    description: "Spain is the gateway to the Schengen zone — a single visa unlocks 27 European countries. From the architectural marvels of Barcelona and the flamenco streets of Seville to the beaches of Costa del Sol and the art of Madrid's Prado, Spain offers an unmatched travel experience. flightfare.pk guides you through the Schengen application process including documentation, VFS appointment booking, and biometric submission.",
    extraInfo: "A Spain Schengen visa is your most flexible European travel document — once approved, you can visit all Schengen member states within the validity period without additional visas.",
  },
  {
    slug: "united-kingdom-visit-visa",
    country: "United Kingdom",
    title: "United Kingdom Visit Visa",
    tagline: "Big Ben, the Highlands, and centuries of history — the UK is calling",
    heroImage: "https://readdy.ai/api/search-image?query=United%20Kingdom%20London%20Tower%20Bridge%20Westminster%20Palace%20Thames%20River%20golden%20hour%20sunset%20landmark%20travel%20destination%20beautiful%20iconic%20British%20cityscape%20dramatic%20sky%20reflections&width=1200&height=500&seq=visa-detail-uk-hero&orientation=landscape",
    bannerImage: "https://readdy.ai/api/search-image?query=Scotland%20Highlands%20misty%20mountains%20lake%20Glen%20Coe%20dramatic%20landscape%20travel%20destination%20epic%20wilderness%20green%20hills%20dramatic%20clouds%20stunning%20scenery&width=600&height=400&seq=visa-detail-uk-side&orientation=landscape",
    price: "60,000",
    processingTime: "3 – 8 weeks",
    validity: "6 months standard (up to 10 years)",
    stayDuration: "Up to 6 months per visit",
    visaType: "Standard Visitor Visa",
    requirements: [
      "Valid Pakistani passport",
      "Online UK visa application (UKVI)",
      "Biometric enrolment at VFS",
      "Bank statements (last 6 months, strong balance)",
      "Confirmed return flight ticket",
      "Hotel or accommodation proof",
      "Employment letter or business documents",
      "Property ownership or tenancy agreement",
      "Invitation letter (if visiting family/friends)",
    ],
    highlights: [
      "Visit England, Scotland, Wales & Northern Ireland",
      "10-year multiple entry available",
      "Covers business and tourism",
      "Includes offshore islands visits",
    ],
    description: "The United Kingdom remains one of the most popular destinations for Pakistani travellers — whether visiting family, exploring London's world-class museums, touring the Scottish Highlands, or watching a Premier League match. The Standard Visitor Visa allows you to stay up to 6 months and is available as a 2-year, 5-year, or 10-year multiple-entry visa. flightfare.pk takes the guesswork out of UK visa applications with expert documentation support.",
    extraInfo: "The UK left the EU and is NOT part of the Schengen zone. You need a separate UK visa even if you hold a Schengen visa. Our team can help you apply for both simultaneously.",
  },
  {
    slug: "united-states-visit-visa",
    country: "United States",
    title: "United States Visit Visa",
    tagline: "New York to LA, the Grand Canyon to Miami — America is massive",
    heroImage: "https://readdy.ai/api/search-image?query=United%20States%20Grand%20Canyon%20aerial%20view%20golden%20sunset%20dramatic%20rock%20formations%20Colorado%20River%20vast%20landscape%20travel%20destination%20iconic%20American%20national%20park%20breathtaking&width=1200&height=500&seq=visa-detail-usa-hero&orientation=landscape",
    bannerImage: "https://readdy.ai/api/search-image?query=New%20York%20City%20Manhattan%20skyline%20aerial%20night%20lights%20skyscrapers%20Central%20Park%20vibrant%20travel%20destination%20iconic%20American%20cityscape%20stunning%20beauty&width=600&height=400&seq=visa-detail-usa-side&orientation=landscape",
    price: "50,000",
    processingTime: "4 – 12 weeks (interview required)",
    validity: "10 years (typical)",
    stayDuration: "Up to 6 months per visit (determined at port of entry)",
    visaType: "B1/B2 Tourist & Business Visa",
    requirements: [
      "Valid Pakistani passport",
      "DS-160 online application form",
      "MRV visa fee payment (USD 185)",
      "Passport-size photograph (US specifications)",
      "US Embassy interview appointment",
      "Bank statements (last 6 months)",
      "Employment letter & proof of income",
      "Property/asset documents",
      "Travel itinerary",
      "Prior travel history (beneficial)",
    ],
    highlights: [
      "Covers all 50 US states",
      "10-year multiple entry typical",
      "B1 (business) + B2 (tourism) combined",
      "Biometric data collected at interview",
    ],
    description: "The United States B1/B2 visa is one of the most coveted travel documents for Pakistani nationals. From New York's iconic skyline and Hollywood to the Grand Canyon, Yellowstone, and Disney World — the US has something extraordinary at every turn. The application process involves an embassy interview, and approval depends on strong ties to Pakistan. flightfare.pk provides complete DS-160 preparation, document guidance, and interview coaching to maximise your chances.",
    extraInfo: "US visa applications require an in-person interview at the US Embassy in Islamabad or the Consulate in Karachi/Lahore. Our team helps you prepare compelling answers and supporting documents.",
  },
  {
    slug: "azerbaijan",
    country: "Azerbaijan",
    title: "Azerbaijan e-Visa",
    tagline: "Flame Towers, the Caspian Sea, and old Baku — Eastern Europe\'s hidden gem",
    heroImage: "https://readdy.ai/api/search-image?query=Azerbaijan%20Baku%20Flame%20Towers%20night%20illuminated%20glowing%20old%20city%20Icheri%20Sheher%20Caspian%20Sea%20blend%20modern%20ancient%20architecture%20travel%20destination%20vibrant%20dramatic%20cityscape&width=1200&height=500&seq=visa-detail-aze-hero&orientation=landscape",
    bannerImage: "https://readdy.ai/api/search-image?query=Azerbaijan%20Gobustan%20mud%20volcanoes%20ancient%20rock%20carvings%20national%20park%20travel%20adventure%20scenic%20destination%20natural%20landscape%20Caucasus%20region%20unique&width=600&height=400&seq=visa-detail-aze-side&orientation=landscape",
    price: "12,500",
    processingTime: "3 – 5 business days",
    validity: "90 days from issue",
    stayDuration: "Up to 30 days",
    visaType: "e-Visa (Single Entry)",
    requirements: [
      "Valid Pakistani passport (min. 3 months validity)",
      "Completed online e-Visa application (ASAN Visa)",
      "Passport scan (bio-data page)",
      "Digital passport-size photograph",
      "Credit/debit card for e-Visa fee (USD 26 approx.)",
      "Hotel booking confirmation",
      "Return air ticket",
    ],
    highlights: [
      "100% online — no embassy visit needed",
      "Visa sent directly to your email",
      "Fast approval in 3–5 days",
      "No original passport submission required",
    ],
    description: "Azerbaijan is one of the easiest and most affordable destinations for Pakistani travellers. The country sits at the crossroads of Eastern Europe and Western Asia, offering a stunning blend of ancient heritage and ultra-modern architecture. Baku's Flame Towers, the walled Old City (Icheri Sheher), the Caspian Sea boulevard, and day trips to Gobustan and Sheki make it a fantastic short-break destination. The e-Visa is 100% online — no embassy visit, no original document submission.",
    extraInfo: "Azerbaijan\'s ASAN Visa portal (evisa.gov.az) processes applications entirely online. flightfare.pk handles the full application on your behalf — just send us your passport scan and photo.",
  },
  {
    slug: "thailand",
    country: "Thailand",
    title: "Thailand Tourist Visa",
    tagline: "Temples, beaches, street food, and smiles — Thailand delivers it all",
    heroImage: "https://readdy.ai/api/search-image?query=Thailand%20Phi%20Phi%20Islands%20aerial%20view%20crystal%20clear%20turquoise%20water%20limestone%20cliffs%20tropical%20paradise%20travel%20destination%20stunning%20beach%20landscape%20golden%20sunshine&width=1200&height=500&seq=visa-detail-thai-hero&orientation=landscape",
    bannerImage: "https://readdy.ai/api/search-image?query=Thailand%20Bangkok%20Grand%20Palace%20Wat%20Phra%20Kaew%20golden%20temple%20ornate%20architecture%20sunset%20travel%20destination%20vibrant%20colorful%20spiritual%20beautiful%20Southeast%20Asia&width=600&height=400&seq=visa-detail-thai-side&orientation=landscape",
    price: "17,500",
    processingTime: "5 – 10 working days",
    validity: "3 months from issue",
    stayDuration: "Up to 60 days (extendable by 30 days)",
    visaType: "Tourist Visa (TR)",
    requirements: [
      "Valid Pakistani passport (min. 6 months validity)",
      "Completed Thailand visa application form",
      "2 recent passport-size photographs",
      "Bank statements (last 3 months, min. PKR 300,000)",
      "Confirmed return flight ticket",
      "Hotel bookings for entire stay",
      "Employment letter or business proof",
      "Travel itinerary",
    ],
    highlights: [
      "Single and double entry options",
      "Extendable at any immigration office",
      "Covers Bangkok, Phuket, Chiang Mai & more",
      "Apply through Royal Thai Embassy Islamabad",
    ],
    description: "Thailand is perennially one of the top destinations for Pakistani travellers — and for good reason. From the golden temples of Bangkok and the party beaches of Phuket to the misty mountains of Chiang Mai and the island paradise of Koh Samui, Thailand packs extraordinary variety into one country. The food alone is worth the trip. flightfare.pk processes Thailand tourist visas efficiently with strong document preparation and follow-up with the embassy.",
    extraInfo: "Thailand also offers a 30-day visa-on-arrival for Pakistani passport holders, but it comes with queues and uncertainty. A pre-arranged tourist visa gives you more flexibility and a smoother arrival.",
  },
  {
    slug: "singapore",
    country: "Singapore",
    title: "Singapore Tourist Visa",
    tagline: "Gardens by the Bay, hawker food, and a skyline like no other",
    heroImage: "https://readdy.ai/api/search-image?query=Singapore%20Marina%20Bay%20Sands%20rooftop%20infinity%20pool%20city%20skyline%20golden%20sunset%20Gardens%20by%20the%20Bay%20supertrees%20stunning%20luxury%20travel%20destination%20Southeast%20Asia%20breathtaking&width=1200&height=500&seq=visa-detail-sing-hero&orientation=landscape",
    bannerImage: "https://readdy.ai/api/search-image?query=Singapore%20Sentosa%20island%20Universal%20Studios%20beach%20cable%20car%20tropical%20clean%20modern%20vibrant%20travel%20destination%20colourful%20fun%20family%20vacation%20Southeast%20Asia&width=600&height=400&seq=visa-detail-sing-side&orientation=landscape",
    price: "18,500",
    processingTime: "3 – 7 working days",
    validity: "30 days from arrival",
    stayDuration: "Up to 30 days",
    visaType: "Tourist Visa (Single Entry)",
    requirements: [
      "Valid Pakistani passport (min. 6 months validity)",
      "Completed Singapore visa application form",
      "Recent passport-size photograph",
      "Scanned copy of bio-data passport page",
      "Bank statements (last 3 months)",
      "Confirmed return flight ticket",
      "Hotel booking confirmation",
      "Employment letter or business proof",
    ],
    highlights: [
      "No original passport submission required",
      "Scanned documents accepted",
      "Fast processing 3–7 days",
      "Single and double entry options",
    ],
    description: "Singapore is a world-class city-state that punches far above its weight. In just 719 km², you get world-famous hawker centres, the futuristic Gardens by the Bay, Universal Studios, the stunning Marina Bay Sands, Little India, Chinatown, and some of Asia's best shopping. The Singapore tourist visa for Pakistani passport holders is straightforward — no original passport submission is needed, only scanned copies. flightfare.pk makes the process fast and simple.",
    extraInfo: "Singapore immigration is strict about overstaying. Make sure your itinerary matches your visa duration, and carry proof of accommodation for all nights. Our team ensures your documents are watertight.",
  },
  {
    slug: "malaysia",
    country: "Malaysia",
    title: "Malaysia Tourist Visa",
    tagline: "Petronas Towers, rainforests, and beaches — Malaysia has it all",
    heroImage: "https://readdy.ai/api/search-image?query=Malaysia%20Kuala%20Lumpur%20Petronas%20Twin%20Towers%20illuminated%20night%20skyline%20city%20lights%20blue%20hour%20travel%20destination%20stunning%20modern%20architecture%20iconic%20Southeast%20Asia&width=1200&height=500&seq=visa-detail-mal-hero&orientation=landscape",
    bannerImage: "https://readdy.ai/api/search-image?query=Malaysia%20Langkawi%20island%20beach%20tropical%20paradise%20clear%20water%20limestone%20islands%20aerial%20view%20vibrant%20green%20jungle%20travel%20destination%20beautiful%20Southeast%20Asia&width=600&height=400&seq=visa-detail-mal-side&orientation=landscape",
    price: "14,500",
    processingTime: "5 – 7 working days",
    validity: "3 months from issue",
    stayDuration: "Up to 30 days",
    visaType: "Single Entry Tourist Visa",
    requirements: [
      "Valid Pakistani passport (min. 6 months validity)",
      "Completed Malaysia visa application form",
      "2 recent passport-size photographs",
      "Bank statements (last 3 months, min. PKR 200,000)",
      "Confirmed return flight ticket",
      "Hotel bookings",
      "Employment letter or business proof",
      "Yellow fever vaccination certificate (if applicable)",
    ],
    highlights: [
      "Covers KL, Penang, Langkawi, Kota Kinabalu & more",
      "Affordable and fast processing",
      "Muslim-friendly destination",
      "Easy transit to Thailand or Singapore",
    ],
    description: "Malaysia is one of the most Muslim-friendly and affordable travel destinations in Southeast Asia, making it hugely popular among Pakistani tourists. The Petronas Towers in Kuala Lumpur are iconic, but Malaysia also offers the stunning island beaches of Langkawi, the heritage streets of Penang, the rainforests of Borneo, and incredible food influenced by Malay, Chinese, and Indian cultures. flightfare.pk provides complete Malaysia visa support with quick turnaround.",
    extraInfo: "Malaysia is an excellent base for multi-country trips — it sits between Thailand to the north and Singapore to the south, and Bali (Indonesia) is just a short flight away. Our team can plan the full itinerary with visas for all countries.",
  },
];

export const getVisaBySlug = (slug: string): VisaData | undefined =>
  VISA_LIST.find(v => v.slug === slug);
